import {connect} from 'react-redux';

import {createSelector, makeMapStateToProps} from '../../../../../srcCommon/helpers/reselectHelpers';
import {buildChannelSettingsPath} from '../../../../helpers/models/channelHelpers';
import {ChannelModel} from '../../../../models/api/channelModel';
import {AppState} from '../../../../store/appState';
import {makeGetChannelInbox} from '../../../../store/data/channels/channelsSelectors';
import {toggleDismissChannelErrorsAction} from '../../../../store/data/localPreferences/localPreferencesActions';
import {getHasNewInboxSettingsDetails} from '../../../../store/featureFlags/featureFlagsSelectors';
import {helpCenterUrl, navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {ChannelErrorBanner} from './channelErrorBanner';

/*
 * Props.
 */

interface ChannelErrorBannerContainerProps {
  channel: ChannelModel;
}

/*
 * Selectors.
 */

function getChannel(_state: AppState, props: ChannelErrorBannerContainerProps) {
  return props.channel;
}

function makeGetInbox() {
  const getInbox = makeGetChannelInbox(getChannel);
  return createSelector([getInbox], (inbox) => {
    if (!inbox) {
      throw new Error('An inbox for this channel could not be found');
    }

    return inbox;
  });
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ChannelErrorBannerContainerProps>()(() => ({
  inbox: makeGetInbox(),
  hasNewInboxSettingsDetails: getHasNewInboxSettingsDetails,
}));

export const ChannelErrorBannerContainer = connect(
  mapStateToProps,
  {toggleDismissChannelErrorsAction, navigateToLinkAction},
  (stateProps, dispatchProps, ownProps: ChannelErrorBannerContainerProps) => {
    const {channel} = ownProps;
    const {inbox, hasNewInboxSettingsDetails} = stateProps;

    const onOpenSettingsClick = () => {
      const settingsPath = buildChannelSettingsPath(hasNewInboxSettingsDetails, inbox, channel);
      dispatchProps.navigateToLinkAction(settingsPath);
    };

    const onLinkClick = () => {
      const {error} = channel;

      // If the provided error has a link, use it.
      if (error && error.linkTarget) {
        dispatchProps.navigateToLinkAction(error.linkTarget);
        return;
      }

      // Otherwise, just open the help center.
      dispatchProps.navigateToLinkAction(helpCenterUrl);
    };

    const onDismissClick = () => {
      dispatchProps.toggleDismissChannelErrorsAction([channel.id], true);
    };

    return {
      ...stateProps,
      ...ownProps,
      onOpenSettingsClick,
      onLinkClick,
      onDismissClick,
    };
  },
)(ChannelErrorBanner);
